import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  EditProfileIdentityError,
  IdentityEditScope,
  ProfileIdentityDraft,
  ProfileResponse,
  UpdateProfileIdentityResponse,
} from '../domain/profile-identity-draft.model';
import { initialState } from './edit-profile-identity.state';

/**
 * Edit Profile Identity slice. Reducers are pure state transitions only; the
 * `PATCH /profiles/:id` call lives in the saga, which dispatches these `save*`
 * transitions as it progresses. That is what lets the reducers be unit-tested as
 * `(state, action) => state` with zero mocks.
 */
const slice = createSlice({
  name: 'editProfileIdentity',
  initialState,
  reducers: {
    /**
     * Seed the form from the profile the host is showing, for one scope.
     *
     * - `global` (admin): the draft starts from the REFERENCE names (`edited ??
     *   collected`), never from `first_name`/`last_name`: on a list of a client
     *   licence those are that licence's own spelling (#703), and saving them
     *   would copy it into the name every licence sees. `collected` keeps the
     *   originals so the dialog can offer to put them back.
     * - `licence` (client): the draft starts from the name the licence sees; the
     *   reference is what a cleared field, or a Reset, brings back.
     *
     * The slice is a singleton shared by the drawer and the detail page, so this
     * runs on every open and a previous attempt cannot leak in.
     */
    dialogOpened: {
      reducer: (
        state,
        action: PayloadAction<{ profile: ProfileResponse; scope: IdentityEditScope }>,
      ) => {
        const { profile: p, scope } = action.payload;
        state.profileId = p.id;
        state.scope = scope;
        state.reference = { firstName: p.reference_first_name, lastName: p.reference_last_name };
        state.draft = {
          firstName: scope === 'licence' ? p.first_name : p.reference_first_name,
          lastName: scope === 'licence' ? p.last_name : p.reference_last_name,
          linkedinUrl: p.linkedin_url,
        };
        state.collected = {
          firstName: p.original_first_name,
          lastName: p.original_last_name,
          editedAt: p.name_edited_at,
        };
        state.validationErrors = {};
        state.saveStatus = 'idle';
        state.error = null;
        state.savedIdentity = null;
      },
      prepare: (profile: ProfileResponse, scope: IdentityEditScope = 'global') => ({
        payload: { profile, scope },
      }),
    },

    /**
     * A single field changed; clears that field's validation error. A post-submit
     * api error (e.g. the 409 on the LinkedIn URL) is about the previous value,
     * so it resets here too.
     */
    fieldChanged: (
      state,
      action: PayloadAction<{ field: keyof ProfileIdentityDraft; value: string }>,
    ) => {
      const { field, value } = action.payload;
      state.draft[field] = value;
      delete state.validationErrors[field];
      if (state.error?.kind === 'api') state.error = null;
    },

    /**
     * Put the name back into the field: the collected one for an admin, the
     * reference one for a licence. Not a separate save mode: the backend clears
     * the edit when it receives that value, so this is just a shortcut for
     * typing it again.
     */
    fieldReset: (state, action: PayloadAction<'firstName' | 'lastName'>) => {
      const field = action.payload;
      state.draft[field] =
        state.scope === 'licence' ? state.reference[field] : state.collected[field];
      delete state.validationErrors[field];
      if (state.error?.kind === 'api') state.error = null;
    },

    // ── Save lifecycle (dispatched by the saga) ──
    saveStarted: (state) => {
      state.saveStatus = 'saving';
      state.error = null;
      state.validationErrors = {};
    },
    saveSucceeded: (state, action: PayloadAction<UpdateProfileIdentityResponse>) => {
      state.saveStatus = 'succeeded';
      state.savedIdentity = action.payload;
    },
    saveFailed: (state, action: PayloadAction<EditProfileIdentityError>) => {
      state.saveStatus = 'failed';
      state.error = action.payload;
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },

    /**
     * The host has consumed `savedIdentity` and is closing the dialog.
     *
     * Deliberately NOT `resetForm`: emptying the draft here repaints the fields
     * blank, and the live validation then flashes "First name is required" under
     * them for the length of the dialog's exit transition; an error message as
     * the last thing an admin sees after a save that worked. The draft is left
     * alone; `dialogOpened` re-seeds it on the next open anyway.
     *
     * What must be cleared is `savedIdentity`, because the slice is a singleton:
     * left set, the next host to mount this dialog would read it on mount and
     * fire its own `onSaved`; a phantom "Profile updated" on a page nobody
     * saved anything from.
     */
    saveAcknowledged: (state) => {
      state.savedIdentity = null;
      state.saveStatus = 'idle';
      state.error = null;
      state.validationErrors = {};
    },

    /** Reset to the pristine slice. */
    resetForm: () => initialState,
  },
});

export const editProfileIdentityActions = slice.actions;
export const editProfileIdentityReducer = slice.reducer;
